// src/mobile_pages/contract/Bill.jsx

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getContractTypeAmount,
  getConstructionTypeAmount,
  getCompanyAmount,
  getAllContracts,
} from "../../api/contract/bill";
import NivoBar from "../../components/common/graphs/NivoBar";
import NivoPie from "../../components/common/graphs/NivoPie";
import NivoHorizontalBar from "../../components/common/graphs/NivoHorizontalBar";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";

const Bill = () => {
  const { id: projectId } = useParams();
  const [typeAmount, setTypeAmount] = useState([]);
  const [typeKeys, setTypeKeys] = useState([]);
  const [consAmount, setConsAmount] = useState([]);
  const [consKeys, setConsKeys] = useState([]);
  const [consCompany, setConsCompany] = useState([]);
  const [subCompany, setSubCompany] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [loading, setLoading] = useState(true);

  const [showMonthlyChart, setShowMonthlyChart] = useState(false);
  const [showPieChart, setShowPieChart] = useState(false);
  const [showCompareChart, setShowCompareChart] = useState(false);
  const [activeChartTab, setActiveChartTab] = useState("type");

  useEffect(() => {
    if (!projectId) return;
    async function fetchData() {
      try {
        const [typeRes, consRes, compRes, allRes] = await Promise.all([
          getContractTypeAmount(projectId),
          getConstructionTypeAmount(projectId),
          getCompanyAmount(projectId),
          getAllContracts(projectId, page, pageSize),
        ]);

        const tKeys = typeRes.map((s) => s.id);
        setTypeKeys(tKeys);
        const mergedType = {};
        typeRes.forEach((series) => {
          series.data.forEach((point) => {
            const month = point.x;
            if (!mergedType[month]) {
              mergedType[month] = { month };
              tKeys.forEach((k) => (mergedType[month][k] = 0));
            }
            mergedType[month][series.id] = point.y ?? 0;
          });
        });
        setTypeAmount(
          Object.values(mergedType).sort(
            (a, b) => new Date(a.month) - new Date(b.month)
          )
        );

        const cKeys =
          consRes.length > 0
            ? Object.keys(consRes[0]).filter((k) => k !== "month")
            : [];
        setConsKeys(cKeys);
        setConsAmount(consRes);

        setConsCompany(
          (compRes["시공"] || []).map((c) => ({
            id: c.company,
            value: c.value,
          }))
        );
        setSubCompany(
          (compRes["하도급"] || []).map((s) => ({
            id: s.company,
            value: s.value,
          }))
        );

        setContracts(allRes || []);
        setSelectedContracts(allRes.slice(0, 5) || []);
        setTotal(allRes.count || 0);
      } catch (err) {
        console.error("비용 데이터 불러오기 실패:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [projectId, page]);

  const handleRowClick = (row) => {
    setSelectedContracts((prev) => {
      const exists = prev.find((c) => c.id === row.id);
      if (exists) return prev.filter((c) => c.id !== row.id);
      return [...prev, row];
    });
  };

  const totalAmount = contracts.reduce((sum, c) => sum + (c.amount || 0), 0);

  const barData = selectedContracts
    .slice()
    .sort((a, b) => a.amount - b.amount)
    .map((c) => ({ id: c.id, name: c.title, amount: c.amount }));

  if (loading) {
    return (
      <div className="mobile-page-root mb-16">
        <div className="mobile-page-root-inner space-y-3">
          <div className="animate-pulse bg-white rounded-lg p-6 border">
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
            <div className="h-8 bg-gray-100 rounded w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-4 border min-w-0">
          <div className="flex items-center gap-3 mb-3 min-w-0">
            <div className="min-w-0 flex-1 overflow-hidden">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                지출/청구 관리
              </h2>
              <p className="text-gray-500 text-xs">총 {contracts.length}건</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border">
            <div className="text-xs text-gray-500 mb-1">계약 총 사용금액</div>
            <div className="text-xl font-bold text-gray-900">
              {Number(totalAmount).toLocaleString("ko-KR")}원
            </div>
          </div>
        </div>

        {/* 월별 차트 */}
        <div className="bg-white rounded-lg border overflow-hidden min-w-0">
          <button
            onClick={() => setShowMonthlyChart(!showMonthlyChart)}
            className="w-full px-4 py-3 flex items-center justify-between min-w-0"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-gray-700">
                월별 계약 금액
              </span>
            </div>
          </button>

          {showMonthlyChart && (
            <div className="px-4 pb-4 border-t">
              <div className="flex mt-3 mb-3 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveChartTab("type")}
                  className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                    activeChartTab === "type"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500"
                  }`}
                >
                  계약 구분별
                </button>
                <button
                  onClick={() => setActiveChartTab("cons")}
                  className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${
                    activeChartTab === "cons"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500"
                  }`}
                >
                  공종별
                </button>
              </div>

              <div className="w-full">
                {activeChartTab === "type" ? (
                  <NivoBar
                    data={typeAmount}
                    keys={typeKeys}
                    indexBy="month"
                    title=""
                    groupMode="stacked"
                    height={220}
                  />
                ) : (
                  <NivoBar
                    data={consAmount}
                    keys={consKeys}
                    indexBy="month"
                    title=""
                    groupMode="stacked"
                    height={220}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* 회사별 파이차트 */}
        <div className="bg-white rounded-lg border overflow-hidden min-w-0">
          <button
            onClick={() => setShowPieChart(!showPieChart)}
            className="w-full px-4 py-3 flex items-center justify-between min-w-0"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-gray-700">
                회사별 금액 분포
              </span>
            </div>
          </button>

          {showPieChart && (
            <div className="px-4 pb-4 border-t space-y-4">
              <div className="pt-3">
                <div className="text-xs font-medium text-gray-500 mb-2">
                  시공 계약
                </div>
                <NivoPie data={consCompany} title="" donut height={180} />
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 mb-2">
                  하도급 계약
                </div>
                <NivoPie data={subCompany} title="" donut height={180} />
              </div>
            </div>
          )}
        </div>

        {/* 선택된 계약 비교 */}
        {barData.length > 0 && (
          <div className="bg-white rounded-lg border overflow-hidden min-w-0">
            <button
              onClick={() => setShowCompareChart(!showCompareChart)}
              className="w-full px-4 py-3 flex items-center justify-between min-w-0"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-medium text-gray-700">
                  계약 금액 비교 ({selectedContracts.length}건)
                </span>
              </div>
            </button>

            {showCompareChart && (
              <div className="px-4 pb-4 border-t pt-3">
                <NivoHorizontalBar
                  data={barData}
                  keys={["amount"]}
                  indexBy="name"
                  height={Math.max(200, barData.length * 40)}
                  title=""
                />
              </div>
            )}
          </div>
        )}

        {/* 계약 리스트 */}
        <div className="space-y-2 min-w-0">
          <div className="flex items-center justify-between px-1 min-w-0">
            <span className="text-sm font-medium text-gray-700">계약 목록</span>
            <span className="text-xs text-gray-500">
              {selectedContracts.length}건 선택됨
            </span>
          </div>

          {contracts.map((row) => {
            const isSelected = selectedContracts.some((c) => c.id === row.id);
            return (
              <button
                key={row.id}
                onClick={() => handleRowClick(row)}
                className={`w-full text-left rounded-lg p-4 border transition-all overflow-hidden min-w-0 ${
                  isSelected
                    ? "bg-blue-50 border-blue-300"
                    : "bg-white hover:border-blue-300"
                }`}
              >
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">
                        {row.construction_type || "미분류"}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">
                        {row.contract_type || "-"}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {row.title || "제목 없음"}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-xs text-gray-500 truncate">
                        {row.company || "-"}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">
                      {row.amount
                        ? `${Number(row.amount).toLocaleString("ko-KR")}원`
                        : "-"}
                    </div>
                    {isSelected && (
                      <span className="text-[10px] text-blue-600">선택됨</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* 페이지네이션 */}
        {contracts.length > 0 && (
          <div className="bg-white rounded-lg p-3 border min-w-0">
            <MobileBoardPagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Bill;
